import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import { ArrowBack, Edit } from "@mui/icons-material";
import { useArticle } from "@/hooks/useKB";
import { KB_CATEGORY_LABELS } from "@/types";
import { brand } from "@/theme";

const STATUS_COLORS: Record<string, "success" | "warning" | "default"> = {
  published: "success",
  draft: "warning",
  archived: "default",
};

export default function KBArticle() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const { data: article, isLoading, error } = useArticle(articleId);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !article) {
    return <Alert severity="error">Article not found.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/kb")}>
          Back to KB
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => navigate(`/kb/${articleId}/edit`)}
        >
          Edit
        </Button>
      </Box>

      {/* Article header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" sx={{ fontFamily: "'Playfair Display', serif", mb: 1 }}>
          {article.title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label={KB_CATEGORY_LABELS[article.category]}
            size="small"
            sx={{ bgcolor: `${brand.forestGreen}15`, color: brand.forestGreen }}
          />
          <Chip label={article.status} size="small" color={STATUS_COLORS[article.status]} />
          <Typography variant="caption" color="text.secondary">
            v{article.version} · Updated {new Date(article.updated_at).toLocaleDateString()}
          </Typography>
          {article.tags?.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Box>
      </Box>

      {/* Article content — rendered HTML from TipTap */}
      <Card>
        <CardContent>
          {article.content_html ? (
            <Box
              sx={{
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: "1rem",
                lineHeight: 1.7,
                "& h1": {
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "1.8rem",
                  mt: 2,
                  mb: 1,
                },
                "& h2": {
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "1.4rem",
                  mt: 2,
                  mb: 1,
                },
                "& h3": {
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "1.2rem",
                  mt: 1.5,
                  mb: 0.5,
                },
                "& p": { mb: 1 },
                "& ul, & ol": { pl: 3, mb: 1 },
                "& blockquote": {
                  borderLeft: `3px solid ${brand.warmGold}`,
                  pl: 2,
                  ml: 0,
                  color: "text.secondary",
                  fontStyle: "italic",
                },
                "& code": {
                  bgcolor: "#f4f3ec",
                  px: 0.5,
                  borderRadius: 0.5,
                  fontFamily: "monospace",
                  fontSize: "0.9em",
                },
                "& pre": {
                  bgcolor: "#1a1a1a",
                  color: "#f0f0f0",
                  p: 2,
                  borderRadius: 1,
                  overflow: "auto",
                },
                "& hr": { border: "none", borderTop: "1px solid", borderColor: "divider", my: 2 },
              }}
              dangerouslySetInnerHTML={{ __html: article.content_html }}
            />
          ) : (
            <Typography color="text.secondary" fontStyle="italic">
              This article has no content yet.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
